import React, { useState } from 'react';
import { Newspaper, Plus, Heart, MessageCircle, Calendar, Pin, Lock, Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { useMembership } from '@/contexts/MembershipContext';

interface NewsPost {
  id: string;
  author: string;
  authorInitials: string;
  category: string;
  title: string;
  body: string;
  date: string;
  likes: number;
  comments: number;
  pinned?: boolean;
  color: string;
  liked?: boolean;
}

const seedPosts: NewsPost[] = [
  {
    id: '1',
    author: 'Margaret Hawthorne',
    authorInitials: 'MH',
    category: 'Announcement',
    title: 'Grandma Eleanor turns 90 next month!',
    body: 'Mark your calendars — we\'re throwing a surprise garden party at the old homestead on May 18th. Please RSVP and bring a favorite memory to share. Eleanor has asked for "no gifts, just grandchildren."',
    date: 'Apr 18, 2026',
    likes: 24,
    comments: 8,
    pinned: true,
    color: 'from-amber-100 to-orange-100 border-amber-300',
  },
  {
    id: '2',
    author: 'David Hawthorne',
    authorInitials: 'DH',
    category: 'New Arrival',
    title: 'Welcome baby Olivia Rose!',
    body: 'Sarah and I are thrilled to introduce our daughter Olivia Rose, born April 12th at 7lbs 4oz. Mom and baby are both healthy and home. Cousin pictures coming soon to the Gallery!',
    date: 'Apr 14, 2026',
    likes: 47,
    comments: 19,
    color: 'from-rose-100 to-pink-100 border-rose-300',
  },
  {
    id: '3',
    author: 'Ruth Caldwell',
    authorInitials: 'RC',
    category: 'Achievement',
    title: 'Ethan accepted to Stanford!',
    body: 'Our nephew Ethan got his early acceptance letter yesterday — full scholarship for engineering. The whole family is bursting with pride. Drop a note of congratulations in the comments!',
    date: 'Apr 10, 2026',
    likes: 38,
    comments: 22,
    color: 'from-emerald-100 to-green-100 border-emerald-300',
  },
  {
    id: '4',
    author: 'Thomas Hawthorne',
    authorInitials: 'TH',
    category: 'Recipe',
    title: 'Great-Grandma\'s pecan pie recipe — finally found!',
    body: 'Cleaning out the attic last weekend and discovered Mama\'s handwritten recipe box. The pecan pie that won every county fair from 1962-1978 is now uploaded to the family vault. Try it for Easter!',
    date: 'Apr 6, 2026',
    likes: 31,
    comments: 14,
    color: 'from-yellow-100 to-amber-100 border-yellow-300',
  },
  {
    id: '5',
    author: 'Linda Hawthorne-Park',
    authorInitials: 'LP',
    category: 'Travel',
    title: 'Family reunion location vote is live',
    body: 'We narrowed it down to three options for July: Lake Tahoe cabins, Charleston beach house, or Asheville mountain retreat. Please cast your vote in the Events section by May 1st.',
    date: 'Apr 2, 2026',
    likes: 19,
    comments: 11,
    color: 'from-purple-100 to-fuchsia-100 border-purple-300',

  },
  {
    id: '6',
    author: 'James Caldwell',
    authorInitials: 'JC',
    category: 'In Memory',
    title: 'Honoring Uncle Robert on his birthday',
    body: 'Today would have been Uncle Bob\'s 78th birthday. Share a favorite story about him below — let\'s keep his laugh alive in this vault forever.',
    date: 'Mar 28, 2026',
    likes: 56,
    comments: 27,
    color: 'from-violet-100 to-purple-100 border-violet-300',
  },
];

const categories = ['Announcement', 'New Arrival', 'Achievement', 'Recipe', 'Travel', 'In Memory', 'Update'];
const categoryColors: Record<string, string> = {
  'Announcement': 'from-amber-100 to-orange-100 border-amber-300',
  'New Arrival': 'from-rose-100 to-pink-100 border-rose-300',
  'Achievement': 'from-emerald-100 to-green-100 border-emerald-300',
  'Travel': 'from-purple-100 to-fuchsia-100 border-purple-300',

  'In Memory': 'from-violet-100 to-purple-100 border-violet-300',
  'Update': 'from-stone-100 to-amber-50 border-stone-300',
};

const FamilyNews: React.FC = () => {
  const { isMember, openMembershipModal } = useMembership();
  const [posts, setPosts] = useState<NewsPost[]>(seedPosts);
  const [filter, setFilter] = useState<string>('All');
  const [composeOpen, setComposeOpen] = useState(false);
  const [newAuthor, setNewAuthor] = useState('');
  const [newCategory, setNewCategory] = useState('Announcement');
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');

  const handleShareClick = () => {
    if (!isMember) {
      openMembershipModal();
      return;
    }
    setComposeOpen(true);
  };

  const handleLike = (id: string) => {
    setPosts(prev => prev.map(p =>
      p.id === id ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
    ));
  };

  const handleComment = (post: NewsPost) => {
    toast.success(`Opening comments for "${post.title}"`, {
      description: `${post.comments} family members have replied`,
    });
  };

  const handlePost = () => {
    if (!newAuthor.trim() || !newTitle.trim() || !newBody.trim()) {
      toast.error('Please fill in your name, a title, and your update');
      return;
    }
    const initials = newAuthor.trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
    const newPost: NewsPost = {
      id: Date.now().toString(),
      author: newAuthor.trim(),
      authorInitials: initials,
      category: newCategory,
      title: newTitle.trim(),
      body: newBody.trim(),
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      likes: 0,
      comments: 0,
      color: categoryColors[newCategory] || categoryColors['Update'],
    };
    setPosts([newPost, ...posts]);
    setNewAuthor('');
    setNewTitle('');
    setNewBody('');
    setNewCategory('Announcement');
    setComposeOpen(false);
    toast.success('Your family news is posted!', { description: 'Everyone in the vault can see it now.' });
  };

  const visiblePosts = filter === 'All' ? posts : posts.filter(p => p.category === filter);
  const sorted = [...visiblePosts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  return (
    <section id="news" className="py-20 bg-gradient-to-b from-[#FAF6F0] via-[#F5EDE0] to-[#FAF6F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100/80 border border-amber-300/60 rounded-full mb-4">
            <Newspaper className="w-4 h-4 text-amber-800" />
            <span className="text-xs font-semibold uppercase tracking-widest text-amber-900">The Family Gazette</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-amber-950 mb-3">
            Family News
          </h2>
          <p className="text-amber-800/80 max-w-2xl mx-auto">
            Births, birthdays, recipes, reunions and remembrances — the everyday stories that bind us together.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {['All', ...categories].map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-all ${
                  filter === cat
                    ? 'bg-amber-900 text-amber-50 border-amber-900 shadow-md'
                    : 'bg-white/70 text-amber-900 border-amber-300/60 hover:bg-amber-100/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <Button
            onClick={handleShareClick}
            className={`gap-2 rounded-full shadow-md ${
              isMember
                ? 'bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50'
                : 'bg-gradient-to-r from-stone-200 to-stone-300 text-stone-700 hover:from-amber-100 hover:to-amber-200 border border-amber-300'
            }`}
          >
            {isMember ? (
              <>
                <Plus className="w-4 h-4" />
                Share Family News
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] uppercase tracking-wider opacity-80">Members only</span>
                  <span className="text-sm font-semibold flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Become a Member to post
                  </span>
                </span>
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sorted.map(post => (
            <Card
              key={post.id}
              className={`relative overflow-hidden border-2 bg-gradient-to-br ${post.color} shadow-md hover:shadow-xl transition-all hover:-translate-y-1 group`}
            >
              {post.pinned && (
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-900 text-amber-50 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full shadow">
                  <Pin className="w-3 h-3" />
                  Pinned
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-700 to-green-800 flex items-center justify-center text-amber-50 font-serif font-bold shadow">
                    {post.authorInitials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-amber-950 truncate">{post.author}</div>
                    <div className="flex items-center gap-1.5 text-xs text-amber-800/70">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </div>
                  </div>
                </div>

                <Badge className="mb-3 bg-white/70 text-amber-900 border border-amber-300/60 hover:bg-white/90">
                  {post.category}
                </Badge>

                <h3 className="font-serif text-xl font-bold text-amber-950 mb-2 leading-tight">
                  {post.title}
                </h3>
                <p className="text-sm text-amber-900/80 leading-relaxed mb-5 line-clamp-4">
                  {post.body}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-amber-900/10">
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                      post.liked ? 'text-rose-600' : 'text-amber-900/70 hover:text-rose-600'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${post.liked ? 'fill-rose-600' : ''}`} />
                    {post.likes}
                  </button>
                  <button
                    onClick={() => handleComment(post)}
                    className="flex items-center gap-1.5 text-sm font-medium text-amber-900/70 hover:text-amber-950 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {post.comments}
                  </button>
                  <div className="flex items-center gap-1 text-xs text-amber-800/60">
                    <Sparkles className="w-3 h-3" />
                    Family
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {visiblePosts.length === 0 && (
          <div className="text-center py-12 text-amber-800/70">
            No posts in this category yet. Be the first to share!
          </div>
        )}
      </div>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg bg-gradient-to-br from-[#FAF6F0] to-[#F5EDE0] border-amber-300">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-amber-950 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-amber-800" />
              Share Family News
            </DialogTitle>
            <DialogDescription className="text-amber-800/70">
              Post an update for everyone in your KinsCircle to see.

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-1.5 block">Your name</label>
              <Input
                value={newAuthor}
                onChange={e => setNewAuthor(e.target.value)}
                placeholder="e.g. Margaret Hawthorne"
                className="bg-white/80 border-amber-300"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-1.5 block">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setNewCategory(cat)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                      newCategory === cat
                        ? 'bg-amber-900 text-amber-50 border-amber-900'
                        : 'bg-white/70 text-amber-900 border-amber-300 hover:bg-amber-100'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-1.5 block">Headline</label>
              <Input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="What's the news?"
                className="bg-white/80 border-amber-300"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-1.5 block">Your update</label>
              <Textarea
                value={newBody}
                onChange={e => setNewBody(e.target.value)}
                placeholder="Tell the family what's happening..."
                rows={5}
                className="bg-white/80 border-amber-300 resize-none"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setComposeOpen(false)}
                className="flex-1 border-amber-300 text-amber-900 hover:bg-amber-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePost}
                className="flex-1 bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50"
              >
                Post to Vault
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FamilyNews;
